import React, { useState, useEffect } from "react"
import { useParams, Link, useHistory } from "react-router-dom"
import { ReactFlowProvider } from "reactflow"
import {
	Grid,
	Typography,
	TextField,
	Tooltip,
	Button,
	IconButton,
	Chip
} from "@material-ui/core"

import {
	ArrowBackIos as GoBackIcon,
	Schedule as LastChangeIcon,
	Delete as DeleteIcon,
	Edit as EditIcon,
	ErrorOutline as ValidationFlowIcon,
	Settings as SettingsIcon,
	Publish as ExportIcon,
	FileCopy as DuplicateIcon,
	LockOutlined as LockIcon
} from "@material-ui/icons"

import {
	Divider,
	InputDialog,
	Loading,
	MoreOptionsMenu,
	Notification,
	PopConfirm,
	ClosePagePrompt,
	ActionDialog,
	JsonEditor
} from "@/components"

import { addNotificationGlobalStyles, removeNotificationGlobalStyles } from "@/components/Notification"
import { usePermissionStateStore } from "@/store/PermissionState"
import { PERMISSION_DENIED_MESSAGE } from "@/components/PermissionGate/BasePermissionGate"

import ActiveFlowReport from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/ActiveFlowReport"
import ValidateFlow from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/ValidateFlow"
import { FlowSwitch } from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/FlowSwitch"
import SaveFlowButton from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/SaveFlowButton"

import { colors } from "@/styles/colors"
import useStyles from "@/pages/Admin/Flow/FlowConstructor/styles"

import useValidation, { ErrorType } from "@/hooks/useValidation"
import useHotjar from "@/hooks/useHotjar"
import useChatBotFlowConstructorStore from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/hooks/useChatBotFlowConstructorStore"
import { useGlobalStateStore } from "@/store/GlobalState"
import useChatBotFlowSave from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/hooks/useChatBotFlowSave"

import ChatBotFlowConstructorStateProvider from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/stores/ChatBotFlowConstructorState"
import GlobalItemSelectionStateProvider from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/stores/GlobalItemSelectionState"

import FlowEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor"

import ChatBotFlowConstructorSkeleton from "@/skeletons/Admin/ChatBotFlowConstructorSkeleton"

import { ShortFlow, FlowConstructorParams } from "@/protocols/chatBotConstructor"

import ChatBotFlowService from "@/services/ChatBotFlow"
import ChatBotConstructorService from "@/services/ChatBotConstructor"
import ErrorHandlerService from "@/services/ErrorHandler"

import { fullDatetime } from "@/utils/time"
import NoteDialog from "@/components/NoteDialog"
import { NoteAdd, NoteView } from "@/assets/icons"
import FlowReports from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/FlowReports"

import ChatBotConfig from "@/pages/Admin/ChatBot/ChatBotList/ChatBotConfig"
import FlowNameInlineEdit from "@/pages/Admin/Flow/FlowConstructor/FlowNameInlineEdit"
import ChatBotGroupPermissionGate from "@/components/PermissionGate/ChatBotGroupPermissionGate"

import copy from "copy-to-clipboard"

import { getFlowConstructorRoute } from "@/utils/routes"

const FlowConstructor: React.FC = () => {
	const classes = useStyles()
	const history = useHistory()
	const globalStateStore = useGlobalStateStore()
	const chatBotFlowConstructorStore = useChatBotFlowConstructorStore()
	const { checkPermission } = usePermissionStateStore()

	const [loading, setLoading] = useState(true)
	const [tooltipMessage, setTooltipMessage] = useState("Só é possível salvar após efetuar alguma alteração no bot.")
	const { chatBotFlowId } = useParams<FlowConstructorParams>()

	const {
		handleToggleActivation
	} = useChatBotFlowSave()

	const hotjar = useHotjar()
	hotjar.setupHotjar()

	const isShowAllChatBotVersions = globalStateStore.user.botVersions.willShow

	const loadInitialData = async () => {
		setLoading(true)
		await chatBotFlowConstructorStore.load()
		setLoading(false)
	}

	const { triggerValidation } = useValidation()

	const [exportedChatBotFlowJson, setExportedChatBotFlowJson] = useState<string>()

	const [isExportChatBotDialogOpen, setIsExportChatBotDialogOpen] = useState(false)
	const [chatBotConfigFlowId, setChatBotConfigFlowId] = useState<number | null>(null)

	const handleFlowNameSave = async (name: string): Promise<void> => {
		try {
			await ChatBotFlowService.update(chatBotFlowConstructorStore.flowData.id, { name } as ShortFlow)
			chatBotFlowConstructorStore.changeChatBotFlow({ name, updated_at: new Date().toISOString() })
			Notification.success({ message: "Nome do bot alterado com sucesso!" })
		} catch {
			Notification.error({ message: "Não foi possível alterar o nome do bot." })
			throw new Error()
		}
	}

	const handleUpdateData = async (chatBotFlow: Partial<ShortFlow>): Promise<void> => {
		await ChatBotFlowService.update(chatBotFlowConstructorStore.flowData.id, chatBotFlow as ShortFlow)

		chatBotFlowConstructorStore.changeChatBotFlow({
			...chatBotFlow,
			updated_at: new Date().toISOString()
		})
	}

	const handleDelete = () => {
		PopConfirm.open({
			title: "Excluir Bot",
			description: "Tem certeza? Essa ação é irreversível.",
			onConfirm: async () => {
				try {
					await ChatBotFlowService.delete(chatBotFlowConstructorStore.flowData.id)
					Notification.success({ message: "Bot excluído com sucesso!" })
					history.push("/admin/automation/flow")
				} catch (error) {
					ErrorHandlerService.handle(error as ErrorType)
					Notification.error({ message: "Não foi possível excluir o bot." })
				}
			},
			confirmButtonText: "EXCLUIR"
		})
	}

	const handleEdit = async () => {
		InputDialog.open({
			title: "Alterar nome do Bot",
			onOk: async (name) => {
				try {
					await handleUpdateData({ name: name as string })
					Notification.success({ message: "Nome do bot alterado com sucesso!" })
					return true
				} catch (error) {
					ErrorHandlerService.handle(error as ErrorType)
					Notification.error({ message: "Não foi possível alterar o nome do bot." })
					return false
				}
			},
			initialValue: chatBotFlowConstructorStore.flowData.name || "",
			fullWidth: true,
			customInputElement: (
				<TextField
					placeholder="Nome do Bot"
					variant="outlined"
					color="primary"
					fullWidth
				/>
			)
		})
	}

	const handleDuplicateChatBotFlow = async (chatBotFlowId: number) => {
		try {
			const duplicatedChatBotFlow = await ChatBotFlowService.duplicate(chatBotFlowId)

			window.location.assign(getFlowConstructorRoute(duplicatedChatBotFlow.id))
		} catch (error) {
			triggerValidation(error as ErrorType)
		}
	}

	const handleOpenChatBotConfig = (chatBotFlowId: number) => {
		setChatBotConfigFlowId(chatBotFlowId)
	}

	const handleOpenExportChatBotDialog = async (chatBotFlowId: number) => {
		const exportedChatBotFlow = await ChatBotFlowService.export(chatBotFlowId)

		const serializedChatBotFlowData = JSON.stringify(exportedChatBotFlow, null, 2)
		setExportedChatBotFlowJson(serializedChatBotFlowData)
		setIsExportChatBotDialogOpen(true)
	}

	const handleExportChatBotClose = () => {
		setIsExportChatBotDialogOpen(false)
	}

	const handleCopyExportedChatBotData = async () => {
		if (exportedChatBotFlowJson) {
			copy(exportedChatBotFlowJson)
			Notification.success({ message: "Dados do fluxo copiado com sucesso!" })
		}
		setIsExportChatBotDialogOpen(false)
	}

	useEffect(() => {
		loadInitialData()
	}, [chatBotFlowId])

	/**
	 * WARNING:
	 * - That's a workaround to change 'Notification' component
	 * style globally inside this page, since we need to make it
	 * positions belows navbar to avoid UX problems.
	 * - Since this component is used in a lot of places inside
	 * this page, it is better to change its behavior globally
	 * instead of passing props for every component.
	 * - When page is mounted, we add the global styles and
	 * when the page is unmounted, we remove the global styles.
	 */
	useEffect(() => {
		addNotificationGlobalStyles({ marginTop: "32px" })

		return () => removeNotificationGlobalStyles()
	}, [])

	return (
		<Grid
			container
			direction="column"
			className={classes.pageContainer}
		>
			<ClosePagePrompt
				activated={chatBotFlowConstructorStore.changed}
				message="Você ainda não salvou suas alterações. Tem certeza que deseja sair?"
			/>

			<Loading
				loading={loading}
				customLoadingElement={<ChatBotFlowConstructorSkeleton />}
			>
				<>
					<Grid
						container
					>
						<Grid
							container
							alignItems="center"
							justify="space-between"
							className={classes.headerContainer}
						>
							<Grid
								item
							>
								<Grid
									container
									alignItems="center"
									justifyContent="flex-start"
									className={classes.headerContent}
									spacing={2}
								>
									<Grid
										item
									>
										<Button
											component={Link}
											to={isShowAllChatBotVersions ? "/admin/bots" : "/admin/automation/flow"}
											startIcon={<GoBackIcon />}
											className={classes.backButton}
										>
											Voltar
										</Button>
									</Grid>

									<Grid
										item
									>
										<ChatBotGroupPermissionGate action="update">
											<FlowNameInlineEdit
												name={chatBotFlowConstructorStore.flowData?.name || ""}
												onSave={handleFlowNameSave}
											/>
										</ChatBotGroupPermissionGate>
									</Grid>

									<Grid
										item
									>
										<IconButton
											onClick={() => {
												NoteDialog.open({
													context: "chat-bot",
													note: {
														id: chatBotFlowConstructorStore.chatBotNotes?.chat_bot_flow_note?.id,
														chat_bot_flow_id: chatBotFlowConstructorStore.flowData?.id,
														content: chatBotFlowConstructorStore.chatBotNotes?.chat_bot_flow_note?.content
													},
													onCreateOrEditCallback: async (note) => {
														chatBotFlowConstructorStore?.setChatBotNotes?.({
															...chatBotFlowConstructorStore.chatBotNotes,
															...{
																chat_bot_flow_note: note
															}
														})
													},
													onDeleteCallback: async () => {
														chatBotFlowConstructorStore?.setChatBotNotes?.({
															...chatBotFlowConstructorStore.chatBotNotes,
															...{
																chat_bot_flow_note: undefined
															}
														})
													}
												})
											}}
										>
											{!chatBotFlowConstructorStore.chatBotNotes?.chat_bot_flow_note?.id ? <NoteAdd opacity={0.65}/> : <NoteView opacity={0.65}/>}
										</IconButton>
									</Grid>

								</Grid>
							</Grid>

							<Grid
								item
								className={classes.headerContent}
							>
								<Grid
									container
									alignItems="center"
									justify="flex-end"
								>
									<Grid item>
										<FlowReports
											instanceId={globalStateStore.instance.instance_id}
											chatBotFlowId={chatBotFlowConstructorStore.flowData?.id}
										/>
									</Grid>

									<Divider orientation="vertical" size={1} />

									<Grid item>
										<ValidateFlow>
											<Grid
												className={classes.validationButton}
											>
												<ValidationFlowIcon fontSize="small" style={{ color: colors.gray[0] }} />
												<Typography className={classes.validationButtonText}>
													{ chatBotFlowConstructorStore.validationErrorsCount }
												</Typography>
											</Grid>
										</ValidateFlow>
									</Grid>

									<Divider orientation="vertical" size={1} />

									<Grid item>
										<ActiveFlowReport
											activeInboxChannelChatFlows={chatBotFlowConstructorStore.flowData.active_inbox_channel_chat_flows}
											changeChatBotFlow={chatBotFlowConstructorStore.changeChatBotFlow}
											chatBotFlowId={chatBotFlowConstructorStore.flowData.id}
											tooltipTitle="Contatos que estão nesse fluxo atualmente"
											buttonTitle="Excluir todos contatos do fluxo"
										/>
									</Grid>

									<Divider orientation="vertical" size={1} />

									{chatBotFlowConstructorStore.flowData?.updated_at && (
										<Grid
											item
										>
											<Grid
												container
												alignItems="center"
											>
												<LastChangeIcon
													className={classes.lastChangeIcon}
													fontSize="small"
												/>

												<Divider orientation="vertical" size={1} />

												<Typography
													variant="caption"
													className={classes.lastChangeText}
												>
													Última alteração: {fullDatetime(new Date(chatBotFlowConstructorStore.flowData?.updated_at))}
												</Typography>
											</Grid>
										</Grid>
									)}

									<Divider orientation="vertical" size={3} />

									<Grid
										item
									>
										<Tooltip
											title={tooltipMessage}
										>
											<Grid
												item
												onMouseMove={() => {
													if (!chatBotFlowConstructorStore.changed) {
														setTooltipMessage("O botão está desabilitado porque nenhuma alteração foi feita no bot.")
													}
												}}
												onMouseLeave={() => setTooltipMessage("")}
											>
												<SaveFlowButton />
											</Grid>
										</Tooltip>
									</Grid>

									{!checkPermission("chat-bot", "update") && (
										<>
											<Divider orientation="vertical" size={2} />

											<Grid
												item
											>
												<Tooltip
													title={PERMISSION_DENIED_MESSAGE}
													arrow
												>
													<Chip
														size="small"
														icon={<LockIcon fontSize="small" />}
														label="Sem permissão para editar"
														className={classes.permissionWarningChip}
													/>
												</Tooltip>
											</Grid>
										</>
									)}

									<Divider orientation="vertical" size={2} />

									<Grid
										item
									>
										<ChatBotGroupPermissionGate action="update">
											<FlowSwitch
												checked={chatBotFlowConstructorStore.flowData?.active}
												onChange={({ target }) => handleToggleActivation(target.checked)}
											/>
										</ChatBotGroupPermissionGate>
									</Grid>

									<Grid
										item
									>
										<MoreOptionsMenu
											closeOnClick={true}
											options={[
												{
													title: "Alterar Nome",
													icon: <EditIcon/>,
													showIcon: true,
													disabled: !checkPermission("chat-bot", "update"),
													disabledTooltip: PERMISSION_DENIED_MESSAGE,
													onClick: handleEdit
												},
												{
													title: "Duplicar Bot",
													icon: <DuplicateIcon />,
													showIcon: true,
													disabled: !checkPermission("chat-bot", "duplicate"),
													disabledTooltip: PERMISSION_DENIED_MESSAGE,
													onClick: () => handleDuplicateChatBotFlow(chatBotFlowConstructorStore.flowData.id)
												},
												{
													title: "Configurações",
													icon: <SettingsIcon />,
													showIcon: true,
													disabled: !checkPermission("chat-bot", "update"),
													disabledTooltip: PERMISSION_DENIED_MESSAGE,
													onClick: () => handleOpenChatBotConfig(chatBotFlowConstructorStore.flowData.id)
												},
												{
													title: "Exportar fluxo",
													icon: <ExportIcon />,
													showIcon: true,
													disabled: !checkPermission("chat-bot", "export"),
													disabledTooltip: PERMISSION_DENIED_MESSAGE,
													onClick: () => handleOpenExportChatBotDialog(chatBotFlowConstructorStore.flowData.id)
												},
												{
													title: "Excluir",
													icon: <DeleteIcon />,
													color: "danger",
													showIcon: true,
													disabled: !checkPermission("chat-bot", "delete"),
													disabledTooltip: PERMISSION_DENIED_MESSAGE,
													onClick: handleDelete
												}
											]}
										/>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Grid>

					<Grid
						container
						className={classes.flowContainer}
					>
						<FlowEditor />
					</Grid>
				</>
			</Loading>

			<ActionDialog
				title="Exportar Bot"
				saveText="Copiar código"
				openDialog={isExportChatBotDialogOpen}
				onSave={handleCopyExportedChatBotData}
				onClose={handleExportChatBotClose}
				fullWidth
			>
				<JsonEditor
					className={classes.disableSelection}
					height="300px"
					defaultValue={exportedChatBotFlowJson}
					readOnly
				/>
			</ActionDialog>

			{chatBotConfigFlowId !== null && (
				<ChatBotConfig
					chatBotFlowId={chatBotConfigFlowId}
					title="CONFIGURAÇÕES DO BOT"
					openDialog={true}
					onClose={() => setChatBotConfigFlowId(null)}
				/>
			)}
		</Grid>
	)
}

const ChatBotFlowConstructorWithContext = () => {
	const { chatBotFlowId } = useParams<{ chatBotFlowId?: string }>()

	return (
		<GlobalItemSelectionStateProvider>
			<ReactFlowProvider>
				<ChatBotFlowConstructorStateProvider
					getFlowData={async () => await ChatBotConstructorService.retrieveFlow(Number(chatBotFlowId))}
					isPreview={false}
					flowId={Number(chatBotFlowId)}
				>
					<FlowConstructor />
				</ChatBotFlowConstructorStateProvider>
			</ReactFlowProvider>
		</GlobalItemSelectionStateProvider>
	)
}

export default ChatBotFlowConstructorWithContext
